export interface TenantDetail {
  id: string;
  name: string;
}

export interface AttackType {
  id: string;
  name: string;
  description?: string;
  attack_class: string; // 'application', 'infrastructure', 'volumetric'
  config_schema: Record<string, any>;
  created_at: string;
  tenants?: string[] | null; // null for global types, array for tenant-specific
  tenant_details?: TenantDetail[] | null; // tenant details with names
}

/** Runbook membership for a scenario (from GET /attacks/scenarios). */
export interface ScenarioRunbookRef {
  id: string;
  name: string;
}

export interface Scenario {
  id: string;
  name: string;
  description?: string;
  tenant_id: string;
  attack_type_id: string;
  configuration: Record<string, any>;
  version: number;
  is_active: boolean;
  /** Per-bot max requests/second cap enforced by the bot runtime; null = unlimited. */
  max_rps?: number | null;
  /** Per-bot max bandwidth cap in bits/second enforced by the bot runtime; null = unlimited. */
  max_bandwidth_bps?: number | null;
  /** Per-bot max packets/second cap enforced by the bot runtime (raw-socket attacks); null = unlimited. */
  max_pps?: number | null;
  created_at: string;
  updated_at: string;
  attack_type: AttackType;
  execution_count?: number;
  last_execution_at?: string;
  /** Runbooks that include this scenario (same tenant). */
  runbooks?: ScenarioRunbookRef[];
}

/** S3 config payload returned by current-scenario (and by GET /scenarios/:id/s3-config). */
export interface ScenarioS3ConfigPayload {
  exists: boolean;
  s3_path?: string;
  last_modified?: string;
  size?: number;
  is_active_scenario?: boolean;
  s3_config?: {
    simulation_name?: string;
    attack_type?: string;
    targets?: string[];
    threads?: number;
    concurrent_requests?: number;
    request_rate?: number;
    payload_size?: number;
    attack_duration?: number;
    full_config?: Record<string, unknown>;
  };
  scenario_details?: {
    id: string;
    name: string;
    description?: string;
    version: number;
    attack_type_name: string;
    attack_class: string;
    created_at: string;
    updated_at: string;
  };
}

/** Live state of a scenario "Run Debug" bot (single ephemeral bot + terminal). */
export interface ScenarioDebugState {
  /** false only from GET when no debug run is active. */
  active?: boolean;
  message?: string;
  bot_id?: string;
  status?: string;
  region_id?: string;
  /** Public IPv4 once the VM is up; null while provisioning. */
  public_ip?: string | null;
  /** True once the bot is running and reachable — safe to open the terminal. */
  ready?: boolean;
  /** Present when status is 'error'. */
  error?: string | null;
}

/** Live state of a "Test Run" bot (CAO-156: single ephemeral bot that runs the
 * attack exactly once and reports its captured output, tenant-scoped since it
 * is not tied to a saved Scenario). */
export interface TestRunState {
  /** false only from GET when no test run is active. */
  active?: boolean;
  message?: string;
  bot_id?: string;
  /** VM lifecycle status: pending, creating, launching, starting, running, ... */
  status?: string;
  region_id?: string;
  /** Public IPv4 once the VM is up; null while provisioning. */
  public_ip?: string | null;
  /** Test-run lifecycle: queued | running | completed | failed. */
  test_run_status?: 'queued' | 'running' | 'completed' | 'failed' | null;
  /** Captured stdout/stderr of the single attack iteration, once available. */
  output?: string | null;
  /** Present when test_run_status is 'failed' (or the bot errored). */
  error?: string | null;
  started_at?: string | null;
  completed_at?: string | null;
}

/** Current scenario plus S3 config in one response (single call). */
export interface CurrentScenarioResponse {
  scenario: Scenario;
  s3_config: ScenarioS3ConfigPayload;
  execution_id?: string | null;
  // All active scenario ids sharing this scenario's name (bots can be split across
  // same-name scenarios). Clients should match every id so none are hidden.
  scenario_ids?: string[];
}

export interface ExecutionLogEntry {
  timestamp: string;
  event_type: 'start' | 'pause' | 'termination' | 'scale' | 'config_change' | 'start_failed' | 'activate' | 'note';
  user_id?: string | null;
  user_email?: string | null;
  provider_id?: string | null;
  provider_name?: string | null;
  region_id?: string | null;
  region_name?: string | null;
  /**
   * For `note` entries: `message` (string) is the operator's text and
   * `attachment_url`/`attachment_filename` (both string) describe an optional
   * screenshot uploaded alongside it. See ExecutionLogChatInput / api.addExecutionLogEntry.
   */
  details?: Record<string, unknown> | null;
}

export interface Execution {
  id: string;
  scenario_id: string;
  tenant_id: string;
  status: 'pending' | 'running' | 'completed' | 'failed' | 'stopped';
  started_at?: string;
  completed_at?: string;
  configuration: Record<string, any>;
  s3_config_path?: string;
  execution_log?: ExecutionLogEntry[];
  impact_bots?: number | null;
  created_at: string;
  updated_at: string;
  scenario: Scenario;
}

export interface ScenarioCreateRequest {
  name: string;
  description?: string;
  attack_type_id: string;
  configuration: Record<string, any>;
  /** Per-bot resource caps enforced by the bot runtime. Omitted/null = unlimited. */
  max_rps?: number | null;
  max_bandwidth_bps?: number | null;
  max_pps?: number | null;
}

export interface ScenarioUpdateRequest {
  name?: string;
  description?: string;
  configuration?: Record<string, any>;
  is_active?: boolean;
  /** Per-bot resource caps enforced by the bot runtime. Omitted = not being updated. */
  max_rps?: number | null;
  max_bandwidth_bps?: number | null;
  max_pps?: number | null;
  /** Explicitly unset the corresponding cap (back to unlimited). */
  clear_max_rps?: boolean;
  clear_max_bandwidth_bps?: boolean;
  clear_max_pps?: boolean;
}

export interface ExecutionCreateRequest {
  scenario_id: string;
}

export interface ExecutionBotIp {
  bot_id: string;
  public_ip: string;
  region_id?: string | null;
  region_name?: string | null;
  provider_name?: string | null;
  status?: string | null;
}

export interface AttackConfigField {
  name: string;
  type: 'string' | 'integer' | 'number' | 'boolean' | 'array' | 'object';
  format?: string;
  description?: string;
  required: boolean;
  minimum?: number;
  maximum?: number;
  default?: any;
  enum?: string[];
  items?: {
    type: string;
  };
  additionalProperties?: {
    type: string;
  };
}

export interface ParsedConfigSchema {
  fields: AttackConfigField[];
  required: string[];
}

export interface AttackTypeCreateRequest {
  name: string;
  description?: string;
  attack_class: 'application' | 'infrastructure' | 'volumetric';
  attack_numeric_id: number; // 1-149 for global, 150-999 for tenant
  config_schema: Record<string, any>;
  scope?: 'tenant' | 'global';
  tenant_ids?: string[]; // Optional list of tenant IDs for tenant-scoped types
}

export interface AttackTypeUpdateRequest {
  name?: string;
  description?: string;
  attack_class?: 'application' | 'infrastructure' | 'volumetric';
  config_schema?: Record<string, any>;
}

// Status color mapping for UI
export const ExecutionStatusColors = {
  pending: 'warning',
  running: 'info',
  completed: 'success',
  failed: 'error',
  stopped: 'default'
} as const;

export const AttackClassColors = {
  volumetric: 'primary',
  infrastructure: 'secondary', 
  application: 'success'
} as const; 